import type { FC, ReactNode } from 'react';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { Icon } from '@launchpad-ui/icons';

import { getMentionResourceUrl, getResourceTypeIconName, parseMessageMentions } from '../../../mentions/mentionUtils';

import * as styles from '../../../mentions/mentions.css';

/**
 * Renders a message string that may contain inline mention markers.
 * Mention markers like @[CPU spike alert](Alert:123) are rendered as
 * styled inline tokens (same appearance as the editor's MentionToken).
 * Plain text segments are rendered as-is with whitespace preserved.
 */
export const InlineContextMessage: FC<{ message: string }> = ({ message }) => {
  const { projectKey } = useProjectId();
  const segments = parseMessageMentions(message);

  if (segments.length === 0 || (segments.length === 1 && segments[0].type === 'text')) {
    return <>{message}</>;
  }

  const parts: ReactNode[] = segments.map((segment, i) => {
    if (segment.type === 'mention' && segment.mention) {
      const { label, resourceType, id } = segment.mention;
      const url = getMentionResourceUrl(projectKey, resourceType, id);

      return (
        <span key={i} className={styles.inlineToken}>
          <Icon name={getResourceTypeIconName(resourceType)} size="tiny" />
          {label}
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.inlineTokenLinkIcon}
              aria-label={`Open ${resourceType.toLowerCase()}`}
            >
              <Icon name="link-external" size="tiny" />
            </a>
          )}
        </span>
      );
    }
    return segment.value;
  });

  return <>{parts}</>;
};
